import { useEffect, useState } from 'react';

import AdsAPI from 'redux/ads/services';
import NoticeModal from '../NoticeModal';
import WelcomeModal from '../WelcomeModal';

// The popup is the only place a client meets a notice - there is no list screen to fall back on -
// so the deck has to be roomy enough that nothing live goes unseen. Ordered soonest-to-expire, so
// if a run of notices ever did exceed this, the ones with time left are the ones that wait.
const DECK_SIZE = 20;

/**
 * Decides which single popup a client meets after signing in.
 *
 * One interruption per sign-in, never two: a live notice wins, and the welcome message is what runs
 * when there is nothing to announce. WelcomeModal itself is untouched - only the question of
 * whether it mounts moved in here.
 *
 * Every sign-in gets the notice again, deliberately. What limits it is the `showWelcomeMsg` flag
 * that gates this component: set once per sign-in, cleared on sign-out and on the 401 that ends a
 * session for OTP. Remembering dismissals across sessions on top of that is what silently retired
 * the notice for good - a client who dismissed it once never saw a live notice again.
 *
 * Nothing renders until the notices request has settled. Deciding while it is still in flight is
 * what would make the welcome message flash up and get swapped a moment later.
 */
const SessionPopup = () => {
  const [state, setState] = useState({ status: 'loading', notices: [] });

  useEffect(() => {
    let live = true;

    AdsAPI.fetchAll({ page: 1, per_page: DECK_SIZE })
      .then(({ data }) => {
        if (!live) return;

        setState({ status: 'ready', notices: data?.ads || [] });
      })
      // A failed request is not the client's problem - fall through to the welcome message rather
      // than leaving them with no popup at all.
      .catch(() => live && setState({ status: 'ready', notices: [] }));

    return () => {
      live = false;
    };
  }, []);

  if (state.status === 'loading') return null;

  if (state.notices.length) {
    return (
      <NoticeModal
        notices={state.notices}
        onClose={() => {
          // Spend this sign-in's one interruption. Without this the notice would be dismissed and
          // the welcome message would take its place on the very next navigation.
          localStorage.removeItem('showWelcomeMsg');
        }}
      />
    );
  }

  return <WelcomeModal />;
};

export default SessionPopup;
